import { useMemo, useState } from 'react'
import { AlertTriangle, Check, ChevronDown, ChevronRight, FileEdit, FilePen, Stamp, XCircle } from 'lucide-react'
import { useAnnexes } from '../annexes/hooks'
import { useAuditLog } from './hooks'
import type { AuditEvent } from './types'

/**
 * Per-contract audit log timeline. FIELD_CHANGED rows whose payload carries
 * {@code triggered_by_annex_id} are folded under the matching ANNEX_CREATED row
 * so the operator sees the publish-time atomic change set in one place.
 *
 * Non-annex rows (standalone FIELD_CHANGED from manual section edits, STAGE_CHANGED,
 * PAYMENT_*, STATUS_*) render inline in chronological order (newest first).
 */
export function AuditLogTab({ contractId }: { contractId: number }) {
  const auditQ = useAuditLog(contractId, 0, 200)
  // Annex events live on entity_type='annex', so we pull them separately to render
  // the ANNEX_CREATED "headline" rows alongside the contract stream.
  const annexesQ = useAnnexes(contractId, true)

  const rows = useMemo(
    () => buildTimeline(auditQ.data?.items ?? [], annexesQ.data ?? []),
    [auditQ.data, annexesQ.data],
  )

  if (auditQ.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Ładowanie historii...</div>
  }
  if (auditQ.error) {
    return <div className="p-6 text-sm text-red-600">Błąd: {String(auditQ.error)}</div>
  }
  if (rows.length === 0) {
    return <div className="p-6 text-sm text-slate-500">Brak zdarzeń w historii.</div>
  }

  return (
    <div className="p-6">
      <h2 className="text-base font-semibold mb-4">Historia umowy</h2>
      <ul className="flex flex-col gap-2">
        {rows.map(r => (
          <TimelineRow key={r.key} row={r} />
        ))}
      </ul>
    </div>
  )
}

function TimelineRow({ row }: { row: TimelineEntry }) {
  if (row.kind === 'annex-group') return <AnnexGroupRow row={row} />
  return <SingleEventRow event={row.event} />
}

function AnnexGroupRow({ row }: { row: AnnexGroupEntry }) {
  const [open, setOpen] = useState(true)
  return (
    <li className="border rounded-md bg-white">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
      >
        {open ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-slate-500" />
              : <ChevronRight size={16} className="mt-0.5 shrink-0 text-slate-500" />}
        <Stamp size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            Aneks #{row.annexNumber ?? '?'}
            {row.reason && <span className="text-slate-500 font-normal"> — {row.reason}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {fmtDate(row.occurredAt)} · autor #{row.actorUserId} · {row.changes.length} zmian
          </div>
        </div>
      </button>
      {open && row.changes.length > 0 && (
        <ul className="border-t bg-slate-50/30 divide-y divide-slate-100">
          {row.changes.map(c => (
            <li key={c.id} className="px-3 py-2 text-sm grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-slate-500 truncate">
                  {String(c.payload.sectionCode ?? '—')} · {String(c.payload.fieldCode ?? '—')}
                </div>
                <div className="text-slate-500 line-through truncate">{fmtCell(c.payload.oldValue)}</div>
              </div>
              <span className="text-xs text-slate-400">→</span>
              <div className="text-slate-900 font-medium truncate">{fmtCell(c.payload.newValue)}</div>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function SingleEventRow({ event }: { event: AuditEvent }) {
  const meta = describe(event)
  return (
    <li className="border rounded-md bg-white px-3 py-2.5 flex items-start gap-3">
      <span className="mt-0.5 shrink-0 text-slate-500">{meta.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <span className="font-medium">{meta.title}</span>
          {meta.detail && <span className="text-slate-600"> — {meta.detail}</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {fmtDate(event.occurredAt)} · autor #{event.actorUserId}
        </div>
      </div>
    </li>
  )
}

// ----- timeline assembly ----------------------------------------------------

type AnnexGroupEntry = {
  kind: 'annex-group'
  key: string
  occurredAt: string
  actorUserId: number
  annexNumber: number | null
  reason: string
  changes: AuditEvent[]
}

type SingleEntry = { kind: 'single'; key: string; event: AuditEvent }

type TimelineEntry = AnnexGroupEntry | SingleEntry

function buildTimeline(
  contractEvents: AuditEvent[],
  annexes: { id: number; annexNumber: number | null; reason: string; publishedAt: string | null; createdByUserId: number; status: string }[],
): TimelineEntry[] {
  // Group FIELD_CHANGED events whose payload references an annex.
  const annexChanges = new Map<number, AuditEvent[]>()
  const ungrouped: AuditEvent[] = []
  for (const e of contractEvents) {
    if (e.eventType === 'FIELD_CHANGED') {
      const annexId = numericPayload(e.payload, 'triggered_by_annex_id')
      if (annexId != null) {
        const list = annexChanges.get(annexId) ?? []
        list.push(e)
        annexChanges.set(annexId, list)
        continue
      }
    }
    ungrouped.push(e)
  }

  const publishedAnnexes = annexes.filter(a => a.status === 'PUBLISHED')
  const groups: AnnexGroupEntry[] = publishedAnnexes.map(a => ({
    kind: 'annex-group',
    key: `annex-${a.id}`,
    occurredAt: a.publishedAt ?? new Date(0).toISOString(),
    actorUserId: a.createdByUserId,
    annexNumber: a.annexNumber,
    reason: a.reason,
    changes: annexChanges.get(a.id) ?? [],
  }))

  const singles: SingleEntry[] = ungrouped.map(e => ({
    kind: 'single',
    key: `evt-${e.id}`,
    event: e,
  }))

  return [...groups, ...singles].sort((a, b) => entryTime(b) - entryTime(a))
}

function entryTime(e: TimelineEntry): number {
  if (e.kind === 'annex-group') return new Date(e.occurredAt).getTime()
  return new Date(e.event.occurredAt).getTime()
}

// ----- presentation helpers -------------------------------------------------

function describe(e: AuditEvent): { title: string; detail: string | null; icon: React.ReactNode } {
  switch (e.eventType) {
    case 'FIELD_CHANGED':
      return {
        title: `Zmiana pola "${String(e.payload.fieldCode ?? '—')}"`,
        detail: `${fmtCell(e.payload.oldValue)} → ${fmtCell(e.payload.newValue)}`,
        icon: <FileEdit size={16} className="text-slate-500" />,
      }
    case 'STAGE_CHANGED':
      return {
        title: 'Zmiana etapu',
        detail: `${String(e.payload.fromStage ?? '—')} → ${String(e.payload.toStage ?? '—')}`,
        icon: <FilePen size={16} className="text-sky-600" />,
      }
    case 'STATUS_CHANGED':
      return {
        title: `Zmiana statusu (${String(e.payload.trackCode ?? '—')})`,
        detail: `${String(e.payload.fromValueCode ?? '—')} → ${String(e.payload.toValueCode ?? '—')}`,
        icon: <FilePen size={16} className="text-slate-500" />,
      }
    case 'PAYMENT_CREATED':
      return { title: 'Zarejestrowano płatność', detail: null, icon: <FilePen size={16} className="text-emerald-600" /> }
    case 'PAYMENT_UPDATED':
      return { title: 'Edytowano płatność', detail: null, icon: <FilePen size={16} className="text-emerald-600" /> }
    case 'PAYMENT_DELETED':
      return { title: 'Usunięto płatność', detail: null, icon: <FilePen size={16} className="text-rose-600" /> }
    case 'FIELD_VERIFIED': {
      const label = String(e.payload.fieldLabel ?? e.payload.fieldCode ?? '—')
      const modes = Array.isArray(e.payload.usedModes) ? e.payload.usedModes.join(', ') : ''
      return {
        title: `Zatwierdzono pole „${label}”`,
        detail: modes ? `tryby: ${modes}` : null,
        icon: <Check size={16} className="text-emerald-600" />,
      }
    }
    case 'FIELD_VERIFICATION_INVALIDATED': {
      const label = String(e.payload.fieldLabel ?? e.payload.fieldCode ?? '—')
      return {
        title: `Cofnięto zatwierdzenie pola „${label}”`,
        detail: 'wartość pola się zmieniła',
        icon: <AlertTriangle size={16} className="text-red-600" />,
      }
    }
    case 'FIELD_VERIFICATION_REVOKED': {
      const label = String(e.payload.fieldLabel ?? e.payload.fieldCode ?? '—')
      const reason = e.payload.reason ? `powód: „${String(e.payload.reason)}”` : null
      return {
        title: `Cofnięto zatwierdzenie pola „${label}”`,
        detail: reason,
        icon: <XCircle size={16} className="text-slate-500" />,
      }
    }
    default:
      return { title: e.eventType, detail: null, icon: <FilePen size={16} className="text-slate-400" /> }
  }
}

function numericPayload(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key]
  if (typeof v === 'number') return v
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v)
  return null
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Tak' : 'Nie'
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })
}
